import React from 'react';
import { Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { OfficialAccommodationLink } from './OfficialAccommodationLink';
import type { RankedOfficialMatch } from '../hooks/useOfficialAccommodationLookup';
import type { OfficialAccommodationStatusId } from '../types/official-accommodation.types';

const STATUS_CLASSES: Record<OfficialAccommodationStatusId, string> = {
  1: 'bg-green-100 text-green-800 hover:bg-green-100',
  2: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  3: 'bg-red-100 text-red-800 hover:bg-red-100',
};

const joinPresent = (parts: Array<string | null | undefined>, separator: string): string =>
  parts.filter((part): part is string => !!part).join(separator);

const formatAddress = (match: RankedOfficialMatch): string => {
  const { street, streetNumber, floor } = match.address;
  const streetLine = joinPresent([street, streetNumber, floor && `piso ${floor}`], ' ');
  return joinPresent([streetLine, match.locality?.name, match.department?.name], ' · ');
};

interface OfficialAccommodationMatchItemProps {
  match: RankedOfficialMatch;
  actionLabel: string;
  selected: boolean;
  onSelect: (match: RankedOfficialMatch) => void;
}

// One official REPAT accommodation: enough detail to recognize the establishment
export const OfficialAccommodationMatchItem: React.FC<OfficialAccommodationMatchItemProps> = ({
  match,
  actionLabel,
  selected,
  onSelect,
}) => {
  const typeLine = joinPresent([match.type?.name, match.category?.name], ' · ');
  const address = formatAddress(match);
  const owner = joinPresent([match.owner.name, match.owner.cuit && `CUIT ${match.owner.cuit}`], ' · ');

  return (
    <li
      className={`flex flex-col gap-2 rounded-md border p-3 text-sm sm:flex-row sm:items-start sm:justify-between ${
        selected ? 'border-green-500 bg-green-50' : 'bg-white'
      }`}
    >
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-gray-900">
            Legajo {match.fileNumber} — {match.establishmentName ?? 'Sin denominación'}
          </span>
          {match.status && (
            <Badge variant="secondary" className={STATUS_CLASSES[match.status.id]}>
              {match.status.name}
            </Badge>
          )}
          {match.sameDepartment && (
            <Badge variant="outline" className="border-blue-300 text-blue-700">
              Mismo departamento
            </Badge>
          )}
        </div>
        {typeLine && <div className="text-gray-600">{typeLine}</div>}
        {address && <div className="text-gray-600">{address}</div>}
        {owner && <div className="text-gray-500">Titular: {owner}</div>}
      </div>
      <div className="flex shrink-0 gap-2">
        <OfficialAccommodationLink accommodationId={match.id} />
        <Button
          type="button"
          size="sm"
          variant={selected ? 'default' : 'outline'}
          onClick={() => onSelect(match)}
        >
          {selected && <Check className="mr-1 h-4 w-4" />}
          {selected ? 'Seleccionado' : actionLabel}
        </Button>
      </div>
    </li>
  );
};
